/**
 * Target Cascade Visualization
 * Visualize how targets cascade from HQ to branches with % splits
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TargetCascade = () => {
  const [orgs, setOrgs] = useState([]);
  const [targets, setTargets] = useState({});
  const [selectedParent, setSelectedParent] = useState(null);
  const [cascadeRules, setCascadeRules] = useState({});
  const [showCascadeModal, setShowCascadeModal] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const token = localStorage.getItem('token');
      const bankResponse = await axios.get(`${API_URL}/api/tenants?tenant_type=bank`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (bankResponse.data.length > 0) {
        const orgResponse = await axios.get(
          `${API_URL}/api/organizations?tenant_id=${bankResponse.data[0].id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setOrgs(orgResponse.data);
        
        // Fetch targets for each org
        orgResponse.data.forEach(org => fetchOrgTargets(org.id));
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchOrgTargets = async (orgId) => {
    try {
      const token = localStorage.getItem('token');
      const year = new Date().getFullYear();
      const response = await axios.get(
        `${API_URL}/api/budget-targets/org/${orgId}/${year}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.length > 0) {
        setTargets(prev => ({ ...prev, [orgId]: response.data[0] }));
      }
    } catch (error) {
      console.log(`No targets for ${orgId}`);
    }
  };

  const initiateCascade = (org) => {
    const children = orgs.filter(o => o.parent_id === org.id);
    if (children.length === 0) {
      alert('This organization has no children to cascade to');
      return;
    }
    
    setSelectedParent(org);
    
    // Initialize equal split
    const equalSplit = 100 / children.length;
    const rules = {};
    children.forEach(child => {
      rules[child.id] = equalSplit;
    });
    setCascadeRules(rules);
    setShowCascadeModal(true);
  };

  const executeCascade = async () => {
    try {
      const token = localStorage.getItem('token');
      const parentTarget = targets[selectedParent.id];
      
      if (!parentTarget) {
        alert('Parent organization has no target to cascade');
        return;
      }
      
      await axios.post(
        `${API_URL}/api/budget-targets/cascade`,
        {
          parent_target_id: parentTarget.id,
          cascade_rules: cascadeRules
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowCascadeModal(false);
      fetchOrganizations();
      alert('Targets cascaded successfully!');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const renderOrgWithTargets = (orgsList, parentId = null, level = 0) => {
    return orgsList
      .filter(org => org.parent_id === parentId)
      .map(org => {
        const target = targets[org.id];
        const hasTarget = !!target;
        const children = orgsList.filter(o => o.parent_id === org.id);
        
        return (
          <div key={org.id} style={{ marginLeft: level * 24 }} className="mb-3">
            <div className={`border-l-4 rounded-lg p-4 ${
              hasTarget ? 'bg-white border-blue-500 shadow' : 'bg-gray-50 border-gray-300'
            }`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">
                      {org.type === 'bank_hq' ? '🏛️' : 
                       org.type === 'branch' ? '🏪' : '📁'}
                    </span>
                    <div>
                      <div className="font-bold text-lg">{org.name}</div>
                      <div className="text-xs text-gray-500">
                        Level {org.level} | {org.type}
                      </div>
                    </div>
                  </div>
                  
                  {hasTarget && (
                    <div className="grid grid-cols-3 gap-2 mt-3">
                      <div className="bg-green-50 rounded p-2">
                        <div className="text-xs text-gray-600">Revenue</div>
                        <div className="font-bold text-green-600">
                          {(target.revenue_target / 1000000).toFixed(1)}M
                        </div>
                      </div>
                      <div className="bg-orange-50 rounded p-2">
                        <div className="text-xs text-gray-600">Expense</div>
                        <div className="font-bold text-orange-600">
                          {(target.expense_target / 1000000).toFixed(1)}M
                        </div>
                      </div>
                      <div className="bg-blue-50 rounded p-2">
                        <div className="text-xs text-gray-600">Profit</div>
                        <div className="font-bold text-blue-600">
                          {(target.profit_target / 1000000).toFixed(1)}M
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {!hasTarget && (
                    <div className="text-sm text-gray-500 italic">
                      No target set
                    </div>
                  )}
                </div>
                
                <div className="flex flex-col gap-2">
                  {hasTarget && children.length > 0 && (
                    <button
                      onClick={() => initiateCascade(org)}
                      className="text-sm bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700"
                    >
                      ⬇️ Cascade
                    </button>
                  )}
                  {target?.cascaded_from && (
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      {target.cascade_percentage}%
                    </span>
                  )}
                </div>
              </div>
            </div>
            {renderOrgWithTargets(orgsList, org.id, level + 1)}
          </div>
        );
      });
  };

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Target Cascade Visualization</h1>
        <p className="text-gray-600 mt-1">
          See how budget targets flow from HQ → Branches and cascade to children
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-blue-800">
          💡 <strong>How it works:</strong> Set target at HQ level, then click "⬇️ Cascade" to distribute to branches.
          Blue percentages show how much each branch received from parent.
        </p>
      </div>

      {/* Organization Tree with Targets */}
      <div className="space-y-2">
        {orgs.length > 0 ? (
          renderOrgWithTargets(orgs)
        ) : (
          <div className="text-center py-12 text-gray-500 bg-white rounded-lg">
            No organizations found. Create organizations first.
          </div>
        )}
      </div>

      {/* Cascade Modal */}
      {showCascadeModal && selectedParent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full">
            <h2 className="text-xl font-bold mb-4">Cascade Target to Children</h2>
            <p className="text-gray-600 mb-4">
              Distribute {selectedParent.name} target to child organizations
            </p>
            
            <div className="mb-4 p-4 bg-blue-50 rounded">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-gray-600">Revenue Target:</div>
                  <div className="font-bold">ETB {(targets[selectedParent.id]?.revenue_target / 1000000).toFixed(1)}M</div>
                </div>
                <div>
                  <div className="text-gray-600">Expense Target:</div>
                  <div className="font-bold">ETB {(targets[selectedParent.id]?.expense_target / 1000000).toFixed(1)}M</div>
                </div>
                <div>
                  <div className="text-gray-600">Profit Target:</div>
                  <div className="font-bold">ETB {(targets[selectedParent.id]?.profit_target / 1000000).toFixed(1)}M</div>
                </div>
              </div>
            </div>

            <div className="space-y-3 mb-4">
              {Object.keys(cascadeRules).map(childId => {
                const child = orgs.find(o => o.id === childId);
                return (
                  <div key={childId} className="flex items-center gap-3">
                    <div className="flex-1 font-medium">{child?.name}</div>
                    <input
                      type="number"
                      step="0.1"
                      value={cascadeRules[childId]}
                      onChange={(e) => setCascadeRules({
                        ...cascadeRules,
                        [childId]: parseFloat(e.target.value)
                      })}
                      className="w-24 border rounded px-3 py-2 text-right"
                    />
                    <span className="w-8">%</span>
                  </div>
                );
              })}
            </div>
            
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <div className="flex justify-between text-sm">
                <span>Total allocation:</span>
                <span className="font-bold">
                  {Object.values(cascadeRules).reduce((sum, val) => sum + val, 0).toFixed(1)}%
                </span>
              </div>
              {Math.abs(Object.values(cascadeRules).reduce((sum, val) => sum + val, 0) - 100) > 0.1 && (
                <p className="text-xs text-yellow-700 mt-1">
                  ⚠️ Must total 100%
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={executeCascade}
                disabled={Math.abs(Object.values(cascadeRules).reduce((sum, val) => sum + val, 0) - 100) > 0.1}
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:bg-gray-300"
              >
                Execute Cascade
              </button>
              <button
                onClick={() => setShowCascadeModal(false)}
                className="bg-gray-200 px-6 py-2 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TargetCascade;
